import React, { useMemo, useState } from "react";
import TowerDayNotice from "./TowerDayNotice";
import { universeClass, universeLabel } from "./model/towerFormat";
import Card from "../ui/Card";
import Input from "../ui/Input";
import TowerFocusMap, { FOCUS_COLORS } from "./map/TowerFocusMap";
import TowerDriverDialog from "./driver/TowerDriverDialog";
import { useTowerDay } from "./hooks/useTowerDay";
import { filterGeoAlerts, groupByDriver, isListedAlert, mapsUrl, geoStatusText } from "./model/geoAlertsView";
import type { TowerDashboard, TowerGeoAlert, TowerGeoAlerts as GeoAlerts } from "../../types/controlTower";
import { activateOnKey } from "./model/towerShared";

const SEVERITY_LABELS: Record<TowerGeoAlert["severity"], string> = {
  main: "Principal",
  light: "Leve",
  doubtfulLocation: "Ubicación dudosa",
  noLocation: "Sin coordenadas",
};

const groupText = (alert: TowerGeoAlert) =>
  alert.groupSize === null ? "" : alert.groupSize === 1 ? "Aislado" : `Grupo de ${alert.groupSize}`;
const fullAddress = (alert: TowerGeoAlert) =>
  [alert.shipment.address, alert.shipment.locality].filter(Boolean).join(", ");

// Pestaña "Alertas geográficas", con la misma lectura que la Torre original:
// contadores, búsqueda, tarjetas por chofer y el mapa del envío seleccionado.
export default function TowerGeoAlerts({
  data,
  today,
  token,
  operationalDateLabel,
  countLabel,
}: {
  data: TowerDashboard;
  today: string;
  token: string | null;
  operationalDateLabel: string;
  countLabel: string;
}) {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // Se guarda el id: el detalle abierto toma cada actualización del día.
  const [openDriverId, setOpenDriverId] = useState<string | null>(null);
  const { day, setDay, data: source, error, retry, pastNote } = useTowerDay(today, data, token);
  const alerts: GeoAlerts | undefined = source?.geoAlerts;
  const drivers = useMemo(() => source?.drivers || [], [source]);
  const openDriver = openDriverId ? drivers.find((d) => d.id === openDriverId) || null : null;
  const items = useMemo(() => alerts?.items || [], [alerts]);
  const visible = useMemo(() => filterGeoAlerts(items, query), [items, query]);
  const groups = useMemo(() => groupByDriver(visible), [visible]);
  const dayInput = (
    <label className="tower-inline-date">
      Día
      <input
        type="date"
        className="tower-date"
        value={day}
        max={today}
        onChange={(e) => {
          setDay(e.target.value);
          setSelectedId(null);
        }}
      />
    </label>
  );
  if (!alerts)
    return (
      <Card className="tower-board">
        <div className="tower-board-heading">
          <h2>Alertas geográficas</h2>
          {dayInput}
        </div>
        <p className="tower-empty" role="status">
          {error || (source ? "El servidor todavía no informa alertas geográficas. Reiniciá el backend." : "Cargando…")}
        </p>
      </Card>
    );
  const openGroupDriver = (driverId: string) => {
    const driver = drivers.find((d) => d.id === driverId);
    if (driver) setOpenDriverId(driver.id);
  };

  const listed = items.filter(isListedAlert);
  const main = listed.filter((a) => a.severity === "main").length;
  const light = listed.filter((a) => a.severity === "light").length;
  // Entregados: ya no se pueden corregir. Pendientes: todavía se puede actuar
  // (no entregados, cancelados ni devueltos).
  const deliveredAlerts = listed.filter((a) => a.shipment.status === "delivered").length;
  const pendingAlerts = listed.filter(
    (a) => !["delivered", "cancelled"].includes(a.shipment.status) && !/DEVUELT/i.test(a.shipment.rawStatus),
  ).length;
  const selected = visible.find((a) => a.id === selectedId) || visible[0] || null;
  const selectedShipments = selected ? drivers.find((d) => d.id === selected.driverId)?.shipments || [] : [];
  const copy = async (alert: TowerGeoAlert) => {
    try {
      await navigator.clipboard.writeText(fullAddress(alert));
      setCopiedId(alert.id);
    } catch {
      setCopiedId(null);
    }
  };

  return (
    <Card className="tower-board">
      <div className="tower-board-heading">
        <h2>Alertas geográficas</h2>
        <div className="tower-board-badges">{dayInput}</div>
      </div>
      <TowerDayNotice error={error} onRetry={retry} pastNote={pastNote} />
      <section className="tower-kpis tower-geo-kpis" aria-label="Resumen de alertas geográficas">
        <Card className="tower-summary-kpi" padding="14px">
          <small>Principales</small>
          <strong className="tower-geo-main">{main}</strong>
        </Card>
        <Card className="tower-summary-kpi" padding="14px">
          <small>Leves</small>
          <strong className="tower-geo-light">{light}</strong>
        </Card>
        <Card className="tower-summary-kpi" padding="14px">
          <small>Entregados</small>
          <strong className="tower-green">{deliveredAlerts}</strong>
        </Card>
        <Card className="tower-summary-kpi" padding="14px">
          <small>Pendientes</small>
          <strong className="tower-geo-pending">{pendingAlerts}</strong>
        </Card>
        <Card className="tower-summary-kpi" padding="14px">
          <small>Choferes involucrados</small>
          <strong>{new Set(listed.map((a) => a.driverId)).size}</strong>
        </Card>
      </section>
      <div className="tower-toolbar tower-geo-search">
        <div className="tower-search">
          <Input
            label="Buscar"
            type="search"
            placeholder="Chofer, cliente, localidad o tracking"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedId(null);
            }}
          />
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="tower-empty">{query.trim() ? "No hay alertas que coincidan." : "Sin alertas."}</p>
      ) : (
        <div className="tower-geo-layout">
          <div className="tower-geo-list">
            {groups.map((group) => (
              <article key={group.driverId} className="tower-geo-driver">
                <header>
                  <div>
                    <strong>{group.driverName}</strong>
                    <span>
                      {group.alerts.length === 1 ? "1 posible error" : `${group.alerts.length} posibles errores`}
                    </span>
                  </div>
                  <button type="button" className="tower-link-button" onClick={() => openGroupDriver(group.driverId)}>
                    Ver reparto →
                  </button>
                </header>
                {group.alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`tower-geo-item${selected?.id === alert.id ? " selected" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedId(alert.id)}
                    onKeyDown={activateOnKey(() => setSelectedId(alert.id))}
                  >
                    <div className="tower-geo-distance-box">
                      <span className={`tower-geo-severity ${alert.severity}`}>{SEVERITY_LABELS[alert.severity]}</span>
                      <strong>{alert.distanceKm} km</strong>
                      <small>fuera del núcleo</small>
                    </div>
                    <div className="tower-geo-item-body">
                      <div className="tower-geo-chips">
                        <span className={`tower-geo-chip ${universeClass(alert.shipment.shippingUniverse)}`}>
                          {universeLabel(alert.shipment.shippingUniverse).toUpperCase()}
                        </span>
                        <span className="tower-geo-chip">{groupText(alert).toUpperCase()}</span>
                        {alert.logistics && <span className="tower-geo-chip">LOGÍSTICA</span>}
                        <span className={`tower-state tower-geo-status ${alert.shipment.status}`}>
                          {geoStatusText(alert)}
                        </span>
                      </div>
                      <strong>{fullAddress(alert) || "Sin dirección"}</strong>
                      <small>
                        {alert.shipment.client} · {alert.shipment.tracking}
                      </small>
                      <p>{alert.reason}</p>
                      <div className="tower-geo-actions" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="tower-map-filter" onClick={() => copy(alert)}>
                          {copiedId === alert.id ? "Copiada" : "Copiar dirección"}
                        </button>
                        <a className="tower-map-filter" href={mapsUrl(alert)} target="_blank" rel="noopener noreferrer">
                          Ver en Maps
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </article>
            ))}
          </div>
          {selected && (
            <div className="tower-geo-detail">
              <div className="tower-geo-detail-head">
                <div>
                  <span className="tower-eyebrow">Chofer seleccionado</span>
                  <h3>
                    {selected.driverName} · {selected.distanceKm} km desde su núcleo habitual
                  </h3>
                </div>
                <div className="tower-geo-legend">
                  <span>
                    <i style={{ background: FOCUS_COLORS.target }} />
                    Envío alejado
                  </span>
                  <span>
                    <i style={{ background: FOCUS_COLORS.route }} />
                    Entregas del reparto
                  </span>
                  <span>
                    <i className="ring" style={{ borderColor: FOCUS_COLORS.core }} />
                    Núcleo habitual
                  </span>
                </div>
              </div>
              <div className="tower-geo-facts">
                <div>
                  <small>Envío seleccionado</small>
                  <strong>{selected.shipment.tracking}</strong>
                  <span>{fullAddress(selected)}</span>
                </div>
                <div>
                  <small>Distancia al núcleo</small>
                  <strong>{selected.distanceKm} km</strong>
                  <span>Límite aplicado: {selected.thresholdKm} km</span>
                </div>
                <div>
                  <small>Radio habitual</small>
                  <strong>{selected.usualRadiusKm} km</strong>
                  <span>Zona donde se concentra el reparto</span>
                </div>
                <div>
                  <small>Lectura operativa</small>
                  <strong>{selected.groupSize === 1 ? "Envío aislado" : `${selected.groupSize} envíos juntos`}</strong>
                  <span>{selected.reason}</span>
                </div>
              </div>
              <TowerFocusMap
                target={selected.shipment}
                others={selectedShipments}
                core={selected.center && { ...selected.center, radiusKm: selected.usualRadiusKm || 0 }}
                targetLabel="Envío alejado"
              />
              <p className="tower-muted tower-geo-footer">
                <strong>{selected.driverName}</strong> · {selectedShipments.length} envíos del día
              </p>
            </div>
          )}
        </div>
      )}
      {openDriver && (
        <TowerDriverDialog
          key={`${day}:${openDriver.id}`}
          driver={openDriver}
          date={day}
          operationalDateLabel={operationalDateLabel}
          countLabel={countLabel}
          onClose={() => setOpenDriverId(null)}
        />
      )}
    </Card>
  );
}
